import React from "react";
import styled from "styled-components";
import { Spinner } from "@cinesys/ui/components";
import SpotterHeader from "./SpotterHeader";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../components/InterfaceAccessNotice";
import { SPOTTER_INTERFACE_ID } from "./config";

/**
 * Per-route Suspense fallback for `spotter/*` routes (`AppRouter.tsx`). Needed because
 * `SpotterHeader` lives INSIDE each screen's lazy-loaded chunk, not in `AppRouter` itself —
 * without a fallback that renders it too, switching to a not-yet-loaded tab blanks the whole
 * page (no header, no padding) until the chunk finishes downloading. `Spinner` self-centers
 * (see its `:before` rule), so `padTop` alone reproduces the spacing each screen's own
 * `Centered` wrapper gives it, with no extra wrapper element needed.
 *
 * Also runs its own `useInterfaceAccess` check (same shared cache as `SpotterHeader`'s own, no
 * extra request) so a denied visitor sees the notice here too, rather than a bare spinner that
 * only turns into the notice once the screen's own chunk finishes downloading.
 */
const Page = styled.div`
  min-height: 100%;
  color: var(--spotterInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

const Wrap = styled.div`
  padding: 0 22px;
`;

const SpotterTabLoadingShell = () => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);

  return (
    <Page>
      <SpotterHeader />
      {access.status === "denied" ? (
        <Wrap>
          <InterfaceAccessNotice reason={access.reason} />
        </Wrap>
      ) : (
        <Spinner padTop />
      )}
    </Page>
  );
};

export default SpotterTabLoadingShell;
